import { createAsyncThunk } from "@reduxjs/toolkit";

import { ajax } from "../../../utils/ajax";
import { IVoucherSearchResults } from "../models";

/**
 * Searches for vouchers by voucher code.
 *
 * This allows CSRs to look up voucher details, check validity, and view usage
 * information. Vouchers can be promotional codes, discount codes, or gift cards.
 * The search returns matching vouchers with their current status, usage limits,
 * and expiration dates.
 *
 * @param code - The voucher code to search for (e.g., "SAVE20", "GIFT-ABC-123")
 * @returns Promise resolving to search results containing matching vouchers
 *
 * @example
 * dispatch(searchVoucherCode("SAVE20"))
 */
export const searchVoucherCode = createAsyncThunk<
    IVoucherSearchResults,
    string,
    { rejectValue: { detail: string } }
>("csr/searchVoucherCode", async (code, { rejectWithValue }) => {
    try {
        const resp = await ajax
            .get("/api/csr/vouchers/")
            .set("Accept", "application/json")
            .query({
                code: code,
            });
        return resp.body;
    } catch (err) {
        const error = err as {
            response?: { body?: unknown };
            message?: string;
        };
        const body = error.response?.body as
            | { detail?: unknown; q?: unknown }
            | string
            | undefined;
        const detail =
            typeof body === "string"
                ? body
                : typeof body?.detail === "string"
                  ? body.detail
                  : typeof body?.q === "string"
                    ? body.q
                    : typeof error.message === "string"
                      ? error.message
                      : "An error occurred while searching for voucher";
        return rejectWithValue({ detail });
    }
});
